import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { apiOutage, OUTAGE_CONFIRM_MS, OUTAGE_RECHECK_MS, probeApi, type OutageMonitor, type OutageState, type ProbeResult } from '../../apollo/outage';
import { useBrand } from '../brand';
import { fetchMaintenance, formatLocalTime, maintenanceView, MAINTENANCE_POLL_MS, MAINTENANCE_URL, type MaintenanceNotice } from './lib';
import './maintenance.css';

/**
 * Follows the outage monitor and, while the API is suspect or down, re-checks
 * it with a cheap probe: `confirmMs` after a first failure (only the probe can
 * confirm the outage, or clear the suspicion when the visitor is offline), and
 * then every `recheckMs` until it answers.
 */
export function useApiOutage(
  monitor: OutageMonitor,
  probe: () => Promise<ProbeResult>,
  { confirmMs = OUTAGE_CONFIRM_MS, recheckMs = OUTAGE_RECHECK_MS } = {},
): OutageState {
  const state = useSyncExternalStore(monitor.subscribe, monitor.state);
  const probeRef = useRef(probe);
  probeRef.current = probe;

  useEffect(() => {
    if (state === 'up') return;
    let live = true;
    const check = () =>
      void probeRef.current().then((result) => {
        if (!live) return;
        if (result === 'up') monitor.reportSuccess();
        else if (result === 'down') monitor.confirmOutage();
        // the visitor's own connection, not the API: drop a suspicion (a confirmed outage stays until the API answers)
        else monitor.clearSuspicion();
      });
    const id = setInterval(check, state === 'suspect' ? confirmMs : recheckMs);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [state, monitor, confirmMs, recheckMs]);

  return state;
}

/** Reads /maintenance.json on load and every `pollMs` after. Null means no maintenance. */
export function usePlannedMaintenance(url = MAINTENANCE_URL, pollMs = MAINTENANCE_POLL_MS): MaintenanceNotice | null {
  const [notice, setNotice] = useState<MaintenanceNotice | null>(null);
  useEffect(() => {
    let live = true;
    const read = () => void fetchMaintenance(url).then((n) => live && setNotice(n));
    read();
    const id = setInterval(read, pollMs);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [url, pollMs]);
  return notice;
}

/** The full-page "we're doing some maintenance" screen, in the brand's look. */
export function MaintenanceScreen({ message, until, overdue = false, timeZone }: { message?: string; until?: Date; overdue?: boolean; timeZone?: string }) {
  const brand = useBrand();
  return (
    <div className="ui-maint" role="main" aria-live="polite">
      <header className="ui-maint-hdr">
        <div className="ui-maint-logo">{brand.logo}</div>
      </header>
      <section className="ui-maint-card">
        <h1 className="ui-maint-title">We're doing some maintenance</h1>
        <p className="ui-maint-lead">{message ?? `${brand.name} will be back shortly.`}</p>
        {until && !overdue && (
          <p className="ui-maint-until">
            Expected back by <strong>{formatLocalTime(until, timeZone)}</strong>
          </p>
        )}
        {overdue && <p className="ui-maint-until">This is taking a little longer than planned. We'll be back as soon as we can.</p>}
        <p className="ui-maint-note">This page checks for us automatically and will reopen {brand.name} as soon as we're back. There's no need to refresh.</p>
      </section>
    </div>
  );
}

const DISMISS_KEY = 'ui-maint-dismissed';

/** A dismissible strip announcing an upcoming maintenance window. */
export function MaintenanceBanner({ startsAt, until, message, timeZone }: { startsAt: Date; until?: Date; message?: string; timeZone?: string }) {
  const brand = useBrand();
  const id = startsAt.toISOString();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === id;
    } catch {
      return false;
    }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, id);
    } catch {
      // private mode: dismiss for this page view only
    }
    setDismissed(true);
  };
  return (
    <div className="ui-maint-banner" role="status">
      <span>
        <strong>Planned maintenance:</strong> {brand.name} will be unavailable from {formatLocalTime(startsAt, timeZone)}
        {until ? ` until ${formatLocalTime(until, timeZone)}` : ''}.{message ? ` ${message}` : ''}
      </span>
      <button type="button" className="ui-maint-dismiss" onClick={dismiss} aria-label="Dismiss maintenance notice">
        ×
      </button>
    </div>
  );
}

/** Don't reload again within this long of the last recovery reload. */
export const RELOAD_GUARD_MS = 5 * 60 * 1000;
const RELOAD_KEY = 'ui-maint-reloaded-at';

/**
 * Reloads once per RELOAD_GUARD_MS. If the probe answers but real queries keep failing, the screen can come and go;
 * without the guard every pass would reload the page again. Inside the guard the screen just clears.
 */
export function reloadOnce(reload: () => void, storage: Pick<Storage, 'getItem' | 'setItem'> | null, now = Date.now()): boolean {
  try {
    if (storage && now - Number(storage.getItem(RELOAD_KEY) || 0) < RELOAD_GUARD_MS) return false;
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // storage blocked (private mode): reload anyway
  }
  reload();
  return true;
}

const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
const reloadPage = () => void reloadOnce(() => window.location.reload(), sessionStore());

/**
 * Wraps a customer app: shows the maintenance screen while the API is down or
 * maintenance is switched on, and an announcement banner before a planned
 * window. When the screen clears, the page reloads so every provider (auth,
 * subscription, Apollo) starts fresh against the recovered API.
 */
export function MaintenanceGate({
  apiUrl,
  children,
  monitor = apiOutage,
  maintenanceUrl = MAINTENANCE_URL,
  onRecover = reloadPage,
}: {
  apiUrl: string;
  children: ReactNode;
  monitor?: OutageMonitor;
  maintenanceUrl?: string;
  onRecover?: () => void;
}) {
  const outage = useApiOutage(monitor, () => probeApi(apiUrl));
  const notice = usePlannedMaintenance(maintenanceUrl);
  const view = maintenanceView(notice, new Date());
  const blocked = outage === 'down' || view.kind === 'active';

  const wasBlocked = useRef(false);
  useEffect(() => {
    if (blocked) wasBlocked.current = true;
    else if (wasBlocked.current) {
      wasBlocked.current = false;
      onRecover();
    }
  }, [blocked, onRecover]);

  if (view.kind === 'active') return <MaintenanceScreen message={view.message} until={view.until} overdue={view.overdue} />;
  if (blocked) return <MaintenanceScreen />;
  return (
    <>
      {view.kind === 'upcoming' && <MaintenanceBanner startsAt={view.startsAt} until={view.until} message={view.message} />}
      {children}
    </>
  );
}
